import React, { useCallback, useEffect, useLayoutEffect, useState, useMemo, useRef } from 'react';
import { useStateSelector, apiTokenSelector } from '../../data/global/dataSelectors';
import { useDispatch } from 'react-redux';
import { AccountManagementDataActions } from '../../data/global/actions/AccountManagementActions';
import {
  AddIcon,
  FormGroup,
  StepperField,
  TextboxField,
  AddressField,
  Text,
  Loading,
  SelectDropdownField,
  useAlerts,
} from '@tradingblock/components';
import { FieldArray, Formik, Field, useFormikContext } from 'formik';
import {
  AllAccountManagementBeneficiaryAccountTypes,
  AllAccountManagementBeneficiaryTypes,
  AllStates,
  BeneficiaryType,
  getGroupedCountryOptions,
  BeneficiaryAccountType,
  AccountManagementBeneficiaryUpdateModel,
  AccountManagementBeneficiaryModel,
  AddressModel,
  BeneficiariesRelationships,
  normalizeCountryCodeForForm,
  Country,
} from '@tradingblock/types';
import _ from 'lodash';
import {
  beneficiaryIndexHasClientErrors,
  buildClientSaveBlockerLines,
  isAddressSectionVisible,
  isBeneficiaryRoleRowComplete,
  isShareSectionVisible,
  scrollAccountManagementBlockIntoView,
  scrollBeneFormSectionToView,
  scrollToFirstBeneficiaryFormError,
} from './beneficiaryUiHelpers';
import { DateField } from '../../components/form/DateField';
import { RemoveAllBeneficiaries } from './modals/RemoveAllBeneficiaries';
import { maskSSN } from '../admin/Applications/services/formatters';
import { getInvalidError, getRequiredError, getSsnItinError } from './modals/Validation';
import { cleanDateModel } from '../../utilities/date';
import { useClearer } from '../../hooks/useClearer';
import {
  validateBeneficiaryPercentages,
  validateAddress,
  clearAllBeneficiaryUspsSessionStorage,
} from '@tradingblock/components';
import Config from '../../config';
import { getResponseCodeDescription } from '../../utilities/action';
import styled from 'styled-components';
/**
 * Note (product / API): Saving beneficiaries always sends the full list; partial or “draft”
 * rows require backend and compliance decisions before a separate API can be built. See
 * beneficiary product docs / roadmap for one-at-a-time save requests.
 */

/** Same card chrome for primary and contingent; role is shown via the badge only. */
const ReadOnlyCard = styled.div`
  border: 1px solid var(--lighten3x);
  border-radius: 8px;
  padding: 1.25rem;
  margin-bottom: 0;
  background: var(--lighten);
  color: rgba(255, 255, 255, 0.95);
  border-left: 4px solid var(--blue-vibrant);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.12);
  container-name: beneficiary-card;
  container-type: inline-size;
  min-width: 0;

  @container beneficiary-card (max-width: 22rem) {
    padding: 1rem;
  }
`;

/**
 * Pads the card grid (margin collapse-safe). Space before the footer is padding, not margin-top on the footer.
 */
const BeneficiariesReadOnlyContent = styled.div.attrs({ className: 'beneficiaries-readonly' })`
  display: flow-root;
  padding-bottom: 2rem;
`;

/** Read-only list footer: primary + destructive CTAs. Do not use `block-action-button` here — it strips btn-primary. */
const BeneReadOnlyFooter = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  row-gap: 0.75rem;
  margin-top: 0;
  padding-top: 1.5rem;
  max-width: min(100%, 72rem);
  width: 100%;
  box-sizing: border-box;

  /* !important: wins over any bundled .btn + .btn / button group margins */
  & > * + * {
    margin-inline-start: 1.25rem !important;
  }
`;

const BeneFooterPrimaryButton = styled.button.attrs({ type: 'button', className: 'btn btn-primary' })`
  min-height: 2.5rem;
  padding: 0.5rem 1.35rem;
  border-radius: 9999px;
  font-weight: 600;
  font-size: 0.8125rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  line-height: 1.2;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.18);
  border: 0;

  &:hover:not(:disabled) {
    opacity: 0.92;
  }

  &:focus-visible {
    outline: 2px solid rgba(255, 255, 255, 0.4);
    outline-offset: 2px;
  }
`;

/**
 * Same affordance as drawer `btn btn-sm btn-danger`, but pill radius matches `BeneFooterPrimaryButton`.
 */
const BeneFooterRemoveButton = styled.button.attrs({ type: 'button', className: 'btn btn-sm btn-danger' })`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 2.5rem;
  padding: 0.5rem 1.25rem;
  border-radius: 9999px;
  font-size: 0.8125rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  line-height: 1.2;
`;

const ReadOnlyCardActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 0.9rem;
  padding-top: 0.9rem;
  border-top: 1px solid var(--lighten3x);
`;

const ReadOnlyEditButton = styled.button.attrs({ type: 'button', className: 'btn btn-sm' })`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.35rem 0.8rem;
  border-radius: 6px;
  font-weight: 600;
  font-size: 0.75rem;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.9);
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid var(--lighten3x);

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.22);
    color: #fff;
  }

  &:focus-visible {
    outline: 2px solid var(--blue-vibrant);
    outline-offset: 2px;
  }

  i {
    font-size: 0.9em;
    opacity: 0.95;
  }
`;

/** On wide viewports, primary and contingent sections sit in two columns when both exist. */
const BeneReadOnlyLayout = styled.div<{ $twoColumn: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  max-width: 100%;

  @media (min-width: 56rem) {
    ${p =>
      p.$twoColumn
        ? `
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      align-items: start;
      column-gap: 1.75rem;
      row-gap: 0;
    `
        : ''}
  }
`;

const BeneReadOnlyColumn = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

const BeneReadOnlyCardStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const SectionHeading = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
  margin: 1.75rem 0 1rem;
  &:first-child {
    margin-top: 0;
  }
`;

const EmptyBeneficiaryState = styled.div`
  border: 1px dashed var(--lighten3x);
  border-radius: 8px;
  padding: 2rem 1.5rem;
  text-align: center;
  background: var(--lighten);
  color: rgba(255, 255, 255, 0.75);
  margin-bottom: 1.25rem;
  max-width: 36rem;
`;

const ReadOnlyCardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  min-width: 0;
`;

const ReadOnlyNameCluster = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  min-width: 0;
  flex: 1 1 12rem;
`;

const ReadOnlyNameText = styled.span`
  font-weight: 700;
  word-break: break-word;
  min-width: 0;
`;

/** Two-column body for individual bene: stacks when the *card* is narrow (not viewport — fixes narrow blocks). */
const ReadOnlyDetailsColumns = styled.div`
  align-items: start;
  display: grid;
  gap: 1.25rem 1.5rem;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 15.5rem), 1fr));
  margin: 0;
  min-width: 0;
`;

const ReadOnlyDetailsColumn = styled.div`
  container-name: bene-detail-col;
  container-type: inline-size;
  min-width: 0;
`;

const ReadOnlyAddressText = styled.div`
  line-height: 1.45;
  overflow-wrap: anywhere;
  word-break: break-word;
`;

const DefRow = styled.div`
  align-items: baseline;
  display: grid;
  font-size: 0.9375rem;
  gap: 0.25rem 0.75rem;
  grid-template-columns: minmax(0, 1fr) auto;
  margin-bottom: 0.45rem;
  min-width: 0;

  /* Narrow *column* (e.g. two-up layout in a wide window) — stack label / value */
  @container bene-detail-col (max-width: 22rem) {
    align-items: flex-start;
    gap: 0.15rem;
    grid-template-columns: 1fr;
  }
`;

const ShareValue = styled.span`
  font-weight: 600;
  font-size: 1.0625rem;
  color: #fff;
`;

const ShareRow = styled(DefRow)`
  border-top: 1px solid var(--lighten3x);
  margin-bottom: 0.35rem;
  margin-top: 0.35rem;
  padding-top: 0.65rem;
`;

const BeneficiaryEditPanel = styled.div`
  border: 1px solid var(--lighten3x);
  border-radius: 8px;
  background: var(--lighten);
  padding: 1.25rem;
  margin-bottom: 1.5rem;
  color: rgba(255, 255, 255, 0.95);
`;

const StickySaveFooter = styled.div`
  position: sticky;
  bottom: 0;
  z-index: 3;
  margin-top: 0.5rem;
  padding: 1rem 0 0.5rem;
  background: linear-gradient(to top, var(--blue-dark) 75%, rgba(0, 18, 54, 0));
  border-top: 1px solid var(--lighten3x);
`;

/** Primary + back actions: back stays next to save while the long form is scrolled. */
const BeneFormStickyActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0;
`;

const FormMaxWidth = styled.div`
  max-width: 46rem;
  width: 100%;
  margin: 0 auto;
`;

const ManageDrawerRoot = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1040;
  pointer-events: ${p => (p.$open ? 'auto' : 'none')};
  visibility: ${p => (p.$open ? 'visible' : 'hidden')};
`;

const ManageDrawerBackdrop = styled.button`
  position: absolute;
  inset: 0;
  border: 0;
  padding: 0;
  background: rgba(0, 10, 30, 0.55);
  cursor: default;
  opacity: ${p => (p.$open ? 1 : 0)};
  transition: opacity 0.2s ease;
`;

const ManageDrawerPanel = styled.aside`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  width: min(100%, 32rem);
  max-width: 100vw;
  background: var(--blue-dark, #0a1628);
  box-shadow: -0.4rem 0 1.5rem rgba(0, 0, 0, 0.45);
  border-left: 1px solid var(--lighten3x);
  transform: translateX(${p => (p.$open ? '0' : '100%')});
  transition: transform 0.22s ease;
`;

const ManageDrawerHeader = styled.header`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  flex-shrink: 0;
  padding: 0.9rem 1rem;
  border-bottom: 1px solid var(--lighten3x);
  background: var(--lighten, rgba(255, 255, 255, 0.04));
`;

const ManageDrawerTitle = styled.h2`
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
  color: #fff;
  line-height: 1.3;
  flex: 1;
  min-width: 0;
`;

const ManageDrawerHeaderActions = styled.div`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: 0.35rem;
`;

const ManageDrawerBody = styled.div`
  flex: 1 1 auto;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
  padding: 0.9rem 1rem 1.25rem;
  min-height: 0;
`;

const ManageDrawerFooter = styled.div`
  flex-shrink: 0;
  padding: 0.85rem 1rem 1rem;
  border-top: 1px solid var(--lighten3x);
  background: linear-gradient(to top, var(--blue-dark) 80%, rgba(0, 18, 54, 0.96));
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
`;

const DrawerFooterActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
`;

const ManageBeneListRow = styled.div<{ $hasError?: boolean }>`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.85rem 1rem;
  border: 1px solid var(--lighten3x);
  border-radius: 8px;
  margin-bottom: 0.75rem;
  background: var(--lighten);
  color: rgba(255, 255, 255, 0.95);
  border-left: 4px solid
    ${p => (p.$hasError ? 'rgba(255, 193, 7, 0.85)' : 'var(--blue-vibrant, #0d6efd)')};
`;

const ManageBeneListRowMain = styled.div`
  min-width: 0;
  flex: 1 1 auto;
`;

const ManageBeneListRowName = styled.div`
  font-weight: 600;
  font-size: 0.98rem;
  word-break: break-word;
  margin-bottom: 0.2rem;
`;

const ManageBeneListRowMeta = styled.div`
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.65);
  line-height: 1.4;
`;

const ManageBeneListRowActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.45rem;
  flex-shrink: 0;
  min-width: 8.5rem;
`;

/** Top-of-form: share totals + what’s blocking (single panel to avoid duplicate messaging). */
const BeneFormStatusPanel = styled.div`
  border: 1px solid var(--lighten3x);
  border-radius: 8px;
  margin-bottom: 1.25rem;
  background: var(--lighten);
  overflow: hidden;
  color: rgba(255, 255, 255, 0.95);
`;

const BeneFormStatusPanelHeader = styled.div`
  padding: 0.5rem 0.9rem 0.45rem;
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgba(255, 255, 255, 0.55);
  font-weight: 600;
  border-bottom: 1px solid var(--lighten3x);
`;

const BeneFormStatusPanelBody = styled.div`
  padding: 0.75rem 1rem;
  font-size: 0.9375rem;
  line-height: 1.45;
`;

const BeneFormStatusBlockerPart = styled.div`
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid var(--lighten3x);
  border-left: 3px solid #ffc107;
  margin-left: -1px;
  padding-left: calc(0.9rem - 2px);
`;

const BeneStatusPill = styled.span<{ $ok: boolean }>`
  display: inline-flex;
  align-items: center;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
  background: ${p => (p.$ok ? 'rgba(40, 167, 69, 0.35)' : 'rgba(255, 193, 7, 0.3)')};
  color: #fff;
  border: 1px solid ${p => (p.$ok ? 'rgba(40, 167, 69, 0.5)' : 'rgba(255, 193, 7, 0.5)')};
`;

const AddressDisclosure = styled.div`
  margin: 0;
  border: 1px solid var(--lighten3x);
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.12);
  overflow: hidden;
`;

const AddressDisclosureButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  text-align: left;
  padding: 0.65rem 0.9rem;
  background: rgba(0, 0, 0, 0.18);
  border: none;
  color: rgba(255, 255, 255, 0.95);
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  gap: 0.5rem;

  &:hover {
    background: rgba(0, 0, 0, 0.25);
  }

  .chevron {
    flex-shrink: 0;
    transition: transform 0.2s ease;
  }
  &.is-open .chevron {
    transform: rotate(90deg);
  }
`;

const AddressDisclosureBody = styled.div`
  padding: 0.75rem 0.9rem 0.9rem;
  border-top: 1px solid var(--lighten3x);
`;

const UspsHint = styled.p`
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.55);
  margin: 0.5rem 0 0;
  line-height: 1.4;
`;

const BeneCardSection = styled.div`
  margin-top: 1.05rem;
  scroll-margin-top: 5.5rem;
  scroll-margin-bottom: 1rem;
  &:first-of-type {
    margin-top: 0;
  }
`;

const BeneCardSectionLabel = styled.h3`
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: rgba(255, 255, 255, 0.5);
  margin: 0 0 0.5rem 0;
`;

const SectionLockedNote = styled.p`
  font-size: 0.8125rem;
  color: rgba(255, 255, 255, 0.52);
  margin: 0.35rem 0 0.9rem;
  font-style: italic;
  line-height: 1.4;
  padding: 0.5rem 0.65rem;
  border-left: 2px solid rgba(255, 255, 255, 0.2);
`;

const TrustNote = styled.p`
  font-size: 0.8125rem;
  color: rgba(255, 255, 255, 0.65);
  margin: -0.25rem 0 1rem;
`;

const DefDt = styled.span`
  color: rgba(255, 255, 255, 0.65);
  font-weight: 500;
`;

const DefDd = styled.span`
  color: rgba(255, 255, 255, 0.95);
  flex: 1;
  min-width: 0;
  text-align: right;
  word-break: break-word;

  @container bene-detail-col (max-width: 22rem) {
    text-align: left;
  }
`;

const PeerShareRowBox = styled.div`
  padding: 0.65rem 0.75rem;
  border: 1px solid var(--lighten3x);
  border-radius: 6px;
  margin-bottom: 0.75rem;
  background: rgba(0, 0, 0, 0.08);

  &:last-child {
    margin-bottom: 0;
  }
`;

const PeerShareBeneficiaryName = styled.div`
  font-weight: 600;
  font-size: 0.9rem;
  word-break: break-word;
  margin-bottom: 0.35rem;
  color: rgba(255, 255, 255, 0.95);
`;

const PeerShareCurrentLabel = styled.div`
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.55);
  margin-bottom: 0.5rem;
`;

/** Grouping inside the Share card so peer editors sit next to the current row's percent field without a nested section shell. */
const PeerShareAdjustmentsGroup = styled.div`
  margin-bottom: 1.25rem;
  scroll-margin-top: 5.5rem;
`;

const BeneficiaryHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
`;

const BeneficiaryTitle = styled.h2`
  margin: 0;
  font-size: 1.15rem;
  color: #fff;
`;

const BeneficiaryAddress = ({ address }: { address: AddressModel }) => (
  <span className="beneficiary-address mute">
    {address.address1 && (
      <>
        {address.address1}
        <br />
      </>
    )}
    {address.address2 && (
      <>
        {address.address2}
        <br />
      </>
    )}
    {address.city}
    {address.state && `, ${address.state}`} {address.postalCode}
    <br />
    {address.country}
  </span>
);

function addressFieldsHaveData(addr: AccountManagementBeneficiaryModel['address'] | undefined): boolean {
  if (!addr) {
    return false;
  }
  return Boolean(
    (addr.address1 && String(addr.address1).trim()) ||
      (addr.address2 && String(addr.address2).trim()) ||
      (addr.city && String(addr.city).trim()) ||
      (addr.state != null && String(addr.state).trim()) ||
      (addr.postalCode != null && String(addr.postalCode).trim()) ||
      (addr.country && String(addr.country).trim())
  );
}

/**
 * When progressive sections unlock, smooth-scroll the viewport to the newly relevant block.
 * Skips the first run so we do not move the page on initial load of prefilled data.
 */
const BeneRowScrollToNext: React.FC<{
  index: number;
  beneficiary: AccountManagementBeneficiaryModel;
}> = ({ index, beneficiary }) => {
  const isRoleComplete = isBeneficiaryRoleRowComplete(beneficiary);
  const isShareUnlocked = isShareSectionVisible(beneficiary);
  const isAddressUnlocked = isAddressSectionVisible(beneficiary);
  const prev = useRef<{
    isRoleComplete: boolean;
    isShareUnlocked: boolean;
    isAddressUnlocked: boolean;
  } | null>(null);
  const skipFirst = useRef(true);

  useEffect(() => {
    if (skipFirst.current) {
      skipFirst.current = false;
      prev.current = { isRoleComplete, isShareUnlocked, isAddressUnlocked };
      return;
    }
    const p = prev.current;
    if (p == null) {
      prev.current = { isRoleComplete, isShareUnlocked, isAddressUnlocked };
      return;
    }

    if (isAddressUnlocked && !p.isAddressUnlocked) {
      scrollBeneFormSectionToView(`bene-${index}-address`);
    } else if (isShareUnlocked && !p.isShareUnlocked) {
      scrollBeneFormSectionToView(`bene-${index}-share`);
    } else if (isRoleComplete && !p.isRoleComplete) {
      const nextId =
        beneficiary.beneficiaryType === BeneficiaryType.Trust
          ? `bene-${index}-trust`
          : `bene-${index}-person`;
      scrollBeneFormSectionToView(nextId);
    }

    prev.current = { isRoleComplete, isShareUnlocked, isAddressUnlocked };
  }, [index, beneficiary.beneficiaryType, isAddressUnlocked, isRoleComplete, isShareUnlocked]);

  return null;
};

const BeneAddressCollapsible: React.FC<{
  sectionId: string;
  hasAddressErrors: boolean;
  showUspsHint: boolean;
  children: React.ReactNode;
}> = ({ sectionId, hasAddressErrors, showUspsHint, children }) => {
  const [isOpen, setIsOpen] = useState(true);
  const bodyId = `${sectionId}-body`;

  const toggle = useCallback(() => {
    setIsOpen(v => !v);
  }, []);

  return (
    <AddressDisclosure>
      <AddressDisclosureButton
        type="button"
        onClick={toggle}
        className={isOpen ? 'is-open' : ''}
        aria-expanded={isOpen}
        aria-controls={isOpen ? bodyId : undefined}
        id={`${sectionId}-toggle`}
      >
        <span>
          {isOpen ? 'Hide address' : 'Add or change address'}
          {hasAddressErrors && !isOpen ? (
            <span className="ml-1 text-warning" style={{ fontSize: '0.75rem', fontWeight: 600 }}>
              (fix needed)
            </span>
          ) : null}
        </span>
        <i className="fal fa-chevron-right chevron" aria-hidden />
      </AddressDisclosureButton>
      {isOpen ? (
        <AddressDisclosureBody id={bodyId} role="region" aria-labelledby={`${sectionId}-toggle`}>
          {children}
          {showUspsHint ? (
            <UspsHint>
              U.S. addresses are checked against USPS. If a <strong>Use provided address</strong> (or similar) control
              appears, use it so your saved address matches the verified format.
            </UspsHint>
          ) : null}
        </AddressDisclosureBody>
      ) : null}
    </AddressDisclosure>
  );
};

const createEmptyBeneficiaryRow = (): AccountManagementBeneficiaryModel =>
  ({
    address: {
      address1: '',
      address2: '',
      city: '',
      postalCode: null,
      country: '',
      state: null,
    },
    beneficiaryAccountType: '',
    beneficiaryType: '',
    dateOfBirth: {
      day: undefined,
      month: undefined,
      year: undefined,
    },
    einSecret: '',
    firstName: '',
    middleName: '',
    lastName: '',
    suffix: '',
    percent: null,
    relationship: '',
    ssnSecret: '',
    trustName: '',
  } as AccountManagementBeneficiaryModel);

function getBeneListRowTitle(b: AccountManagementBeneficiaryModel): string {
  if (b.beneficiaryType === BeneficiaryType.Trust) {
    return b.trustName && String(b.trustName).trim() ? b.trustName : 'Trust (name not entered)';
  }
  if (b.beneficiaryType === BeneficiaryType.Individual) {
    const n = `${b.firstName || ''} ${b.lastName || ''}`.trim();
    return n || 'Individual (name not entered)';
  }
  return 'Beneficiary (not started)';
}

/**
 * Same-role beneficiaries (excluding the drawer row) with editable shares so allocations can be rebalanced while adding
 * or editing one beneficiary—without closing the side panel.
 */
const PeerBeneficiaryShareAdjustments: React.FC<{ drawerBeneficiaryIndex: number }> = ({ drawerBeneficiaryIndex }) => {
  const { values } = useFormikContext<AccountManagementBeneficiaryUpdateModel>();
  const current = values.beneficiaries[drawerBeneficiaryIndex];
  if (current == null) {
    return null;
  }

  const roleType = current.beneficiaryAccountType;
  if (roleType !== BeneficiaryAccountType.Primary && roleType !== BeneficiaryAccountType.Contingent) {
    return null;
  }

  const peers = values.beneficiaries
    .map((b, j) => ({ b, j }))
    .filter(({ b, j }) => j !== drawerBeneficiaryIndex && b.beneficiaryAccountType === roleType);

  if (peers.length === 0) {
    return null;
  }

  const cohortLabel = roleType === BeneficiaryAccountType.Primary ? 'primary' : 'contingent';
  const hintId = `bene-${drawerBeneficiaryIndex}-peer-shares-hint`;

  return (
    <PeerShareAdjustmentsGroup id={`bene-${drawerBeneficiaryIndex}-peer-shares`}>
      <BeneCardSectionLabel>Other {cohortLabel} beneficiaries</BeneCardSectionLabel>
      <p className="small text-muted mb-3" id={hintId}>
        Rebalance these shares as needed, then set this beneficiary&apos;s share below—all {cohortLabel} allocations must total{' '}
        <strong>100%</strong>.
      </p>
      {peers.map(({ b, j }) => {
        const title = getBeneListRowTitle(b);
        const pct = b.percent;
        const pctDisplay = pct != null ? `${pct}%` : '—';
        return (
          <PeerShareRowBox key={`peer-share-${j}`}>
            <PeerShareBeneficiaryName>{title}</PeerShareBeneficiaryName>
            <PeerShareCurrentLabel>Current share: {pctDisplay}</PeerShareCurrentLabel>
            <FormGroup>
              <Field
                component={TextboxField}
                id={`beneficiaries[${j}].percent`}
                label={`Share % · ${title}`}
                type="number"
                min="1"
                max="100"
                step="1"
                aria-describedby={`${hintId} beneficiary-form-help`}
              />
            </FormGroup>
          </PeerShareRowBox>
        );
      })}
    </PeerShareAdjustmentsGroup>
  );
};

/**
 * All fields for one beneficiary (shown inside the manage drawer). Must render under Formik.
 */
const BeneficiaryRowFormFields: React.FC<{
  index: number;
  clearer: ReturnType<typeof useClearer>;
  showScrollNudge: boolean;
}> = ({ index, clearer, showScrollNudge }) => {
  const { values, setValues, errors } = useFormikContext<AccountManagementBeneficiaryUpdateModel>();
  const beneficiary = values.beneficiaries[index];
  if (beneficiary == null) {
    return null;
  }
  const drawerBeneficiaryShareTitle = getBeneListRowTitle(beneficiary);
  return (
    <BeneficiaryEditPanel className="beneficiary-drawer-panel" data-bene-form-index={String(index)}>
      {showScrollNudge ? <BeneRowScrollToNext index={index} beneficiary={beneficiary} /> : null}
      <BeneCardSection id={`bene-${index}-role`}>
        <BeneCardSectionLabel>Role</BeneCardSectionLabel>
        <FormGroup>
          <Field
            component={StepperField}
            id={`beneficiaries[${index}].beneficiaryAccountType`}
            options={AllAccountManagementBeneficiaryAccountTypes}
          />
        </FormGroup>
        <FormGroup>
          <Field
            component={StepperField}
            id={`beneficiaries[${index}].beneficiaryType`}
            options={AllAccountManagementBeneficiaryTypes}
            disabledOptions={[BeneficiaryType.Trust]}
            onChanged={(beneficiaryType: BeneficiaryType) =>
              setValues({
                ...values,
                beneficiaries: _.map(values.beneficiaries, (b, j) => {
                  if (index === j) {
                    const isIndividual = beneficiaryType === BeneficiaryType.Individual;
                    return {
                      ...b,
                      beneficiaryType,
                      relationship: isIndividual ? b.relationship : undefined,
                      firstName: isIndividual ? b.firstName : '',
                      lastName: isIndividual ? b.lastName : '',
                      dateOfBirth: isIndividual ? b.dateOfBirth : {},
                      ssnSecret: isIndividual ? b.ssnSecret : '',
                      trustName: isIndividual ? undefined : b.trustName,
                      einSecret: isIndividual ? undefined : b.einSecret,
                    };
                  }
                  return b;
                }),
              })
            }
          />
        </FormGroup>
        <TrustNote>
          Trust is not available for beneficiary type on this screen. Use Individual for updates here.
        </TrustNote>
        {!isBeneficiaryRoleRowComplete(beneficiary) && (
          <SectionLockedNote>
            Select primary or contingent, then individual or trust, to unlock the next section.
          </SectionLockedNote>
        )}
      </BeneCardSection>

      {isBeneficiaryRoleRowComplete(beneficiary) && beneficiary.beneficiaryType === BeneficiaryType.Individual && (
        <BeneCardSection id={`bene-${index}-person`}>
          <BeneCardSectionLabel>Person</BeneCardSectionLabel>
          <FormGroup>
            <Field
              component={SelectDropdownField}
              id={`beneficiaries[${index}].relationship`}
              isSearchable={false}
              options={BeneficiariesRelationships}
              defaultLabelId="relationship"
              optionsDefaultLabelId="beneficiaryRelationship"
            />
          </FormGroup>
          <FormGroup>
            <Field component={TextboxField} id={`beneficiaries[${index}].firstName`} defaultLabelId="firstName" />
          </FormGroup>
          <FormGroup>
            <Field component={TextboxField} id={`beneficiaries[${index}].middleName`} defaultLabelId="middleName" />
          </FormGroup>
          <FormGroup>
            <Field component={TextboxField} id={`beneficiaries[${index}].lastName`} defaultLabelId="lastName" />
          </FormGroup>
          <FormGroup>
            <Field
              component={SelectDropdownField}
              id={`beneficiaries[${index}].suffix`}
              defaultLabelId="suffix"
              optionsDefaultLabelId="suffix"
              options={['None', 'Jr', 'Sr', 'I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX']}
              isSearchable={false}
              isClearable
            />
          </FormGroup>
          <FormGroup>
            <Field component={DateField} id={`beneficiaries[${index}].dateOfBirth`} defaultLabelId="dateOfBirth" />
          </FormGroup>
          <FormGroup>
            <Field
              component={TextboxField}
              id={`beneficiaries[${index}].ssnSecret`}
              label="SSN/ITIN"
              type="password"
              allowUnmask={true}
            />
          </FormGroup>
        </BeneCardSection>
      )}

      {isBeneficiaryRoleRowComplete(beneficiary) &&
        beneficiary.beneficiaryType === BeneficiaryType.Individual &&
        !isShareSectionVisible(beneficiary) && (
          <SectionLockedNote>
            Complete all person fields (including date of birth and SSN/ITIN) to set the share %.
          </SectionLockedNote>
        )}

      {isBeneficiaryRoleRowComplete(beneficiary) && beneficiary.beneficiaryType === BeneficiaryType.Trust && (
        <BeneCardSection id={`bene-${index}-trust`}>
          <BeneCardSectionLabel>Trust</BeneCardSectionLabel>
          <FormGroup>
            <Field component={TextboxField} id={`beneficiaries[${index}].trustName`} />
          </FormGroup>
          <FormGroup>
            <Field
              component={TextboxField}
              id={`beneficiaries[${index}].einSecret`}
              type="password"
              allowUnmask={true}
            />
          </FormGroup>
        </BeneCardSection>
      )}

      {isBeneficiaryRoleRowComplete(beneficiary) &&
        beneficiary.beneficiaryType === BeneficiaryType.Trust &&
        !isShareSectionVisible(beneficiary) && (
          <SectionLockedNote>Enter the trust name and EIN to set the share %.</SectionLockedNote>
        )}

      {isShareSectionVisible(beneficiary) && (
        <BeneCardSection id={`bene-${index}-share`}>
          <BeneCardSectionLabel>Share</BeneCardSectionLabel>
          <PeerBeneficiaryShareAdjustments drawerBeneficiaryIndex={index} />
          <PeerShareRowBox>
            <BeneCardSectionLabel>This beneficiary</BeneCardSectionLabel>
            <PeerShareBeneficiaryName>{drawerBeneficiaryShareTitle}</PeerShareBeneficiaryName>
            <p className="small text-muted mb-2" style={{ lineHeight: 1.45 }}>
              Applies only to this beneficiary—the entry you are editing in this panel, not those above.
            </p>
            <FormGroup>
              <Field
                component={TextboxField}
                id={`beneficiaries[${index}].percent`}
                label={`Share % · ${drawerBeneficiaryShareTitle}`}
                type="number"
                min="1"
                max="100"
                step="1"
                aria-describedby="beneficiary-form-help"
              />
            </FormGroup>
          </PeerShareRowBox>
          {!isAddressSectionVisible(beneficiary) && (
            <SectionLockedNote style={{ marginTop: '0.65rem' }}>
              Use a whole number from 1–100 for the share, then you can add the address.
            </SectionLockedNote>
          )}
        </BeneCardSection>
      )}

      {isAddressSectionVisible(beneficiary) && (
        <BeneCardSection id={`bene-${index}-address`}>
          <BeneCardSectionLabel>Address</BeneCardSectionLabel>
          <BeneAddressCollapsible
            key={`bene-addr-${index}`}
            sectionId={`bene-form-${index}`}
            hasAddressErrors={Object.keys(errors).some(
              k => k.startsWith(`beneficiaries[${index}].address.`) && (errors as Record<string, string | undefined>)[k]
            )}
            showUspsHint={String(beneficiary.address && beneficiary.address.country) === Country.UnitedStatesOfAmerica}
          >
            <Field
              component={AddressField}
              id={`beneficiaries[${index}].address`}
              labelPrefix="Beneficiary"
              countryProps={{
                options: getGroupedCountryOptions(clearer),
                defaultLabelId: 'address.country',
                optionsDefaultLabelId: 'country',
                labelPrefix: 'Beneficiary',
              }}
              stateProps={{
                options: AllStates,
                defaultLabelId: 'address.state',
                optionsDefaultLabelId: 'state',
                labelPrefix: 'Beneficiary',
              }}
            />
          </BeneAddressCollapsible>
        </BeneCardSection>
      )}
    </BeneficiaryEditPanel>
  );
};

/**
 * Keeps a frozen copy of the beneficiary list in parent state whenever the drawer opens, and clears
 * it when the drawer closes—so the main list can render from a snapshot (see beneListFrozenSnapshot)
 * and stay stable while the user edits in the panel.
 */
const BeneListSnapshotEffect: React.FC<{
  beneficiaries: AccountManagementBeneficiaryModel[];
  drawerIndex: number | null;
  setBeneListFrozenSnapshot: (s: AccountManagementBeneficiaryModel[] | null) => void;
}> = ({ beneficiaries, drawerIndex, setBeneListFrozenSnapshot }) => {
  const beneficiariesRef = useRef(beneficiaries);
  beneficiariesRef.current = beneficiaries;
  useLayoutEffect(() => {
    if (drawerIndex === null) {
      setBeneListFrozenSnapshot(null);
    } else {
      setBeneListFrozenSnapshot(_.cloneDeep(beneficiariesRef.current));
    }
  }, [drawerIndex, setBeneListFrozenSnapshot]);
  return null;
};

export const Beneficiaries: React.FunctionComponent<{}> = () => {
  const accountId = useStateSelector(s => s.account.accountId);
  const beneficiaries = useStateSelector(s => s.accountManagement.accountManagementDetails.details.beneficiaries);
  const beneArr = beneficiaries || [];
  const dispatch = useDispatch();
  const isUpdateLoading = useStateSelector(s => s.accountManagement.update.isLoading);
  const updateData = useStateSelector(s => s.accountManagement.update.data);
  const updateResponseCode = useStateSelector(s => s.accountManagement.update.responseCode);
  const updateLoaded = useStateSelector(s => s.accountManagement.update.isLoaded);
  const selectedLinkedAccountId = useStateSelector(
    s => s.account.selectedLinkedAccount && s.account.selectedLinkedAccount.accountId
  );
  const isLoaded = useStateSelector(s => s.accountManagement.accountDetails.isLoaded);
  const apiToken = useStateSelector(apiTokenSelector);
  const detailsFetching = useStateSelector(s => s.accountManagement.accountManagementDetails.isFetching);
  const detailsError = useStateSelector(s => s.accountManagement.accountManagementDetails.error);

  const effectiveAccountId =
    selectedLinkedAccountId != null ? selectedLinkedAccountId : accountId;
  const notifications = useAlerts();
  const beneficiarySavePendingRef = useRef(false);

  const [manageMode, setManageMode] = useState(false);
  /** When set, a new manage session starts with one empty row (no beneficiaries on file). */
  const [seedEmptyRow, setSeedEmptyRow] = useState(false);
  /** Bumps to remount Formik from server/seed when re-entering manage mode. */
  const [manageFormKey, setManageFormKey] = useState(0);
  /** Index of the beneficiary being edited in the side drawer, or `null` when the drawer is closed. */
  const [drawerIndex, setDrawerIndex] = useState<number | null>(null);
  /**
   * While the drawer is open, the main list shows this snapshot of beneficiaries (captured when the
   * drawer was opened) so the list does not change live as the user types—only after close or save.
   */
  const [beneListFrozenSnapshot, setBeneListFrozenSnapshot] = useState<
    AccountManagementBeneficiaryModel[] | null
  >(null);
  const drawerPanelRef = useRef<HTMLDivElement | null>(null);
  const [showRemoveAll, setShowRemoveAll] = useState(false);
  const [clientSaveValidationAttempt, setClientSaveValidationAttempt] = useState(0);
  const [serverRejectionMessages, setServerRejectionMessages] = useState<string[]>([]);
  const clearer = useClearer();

  const exitManageMode = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setManageMode(false);
    setDrawerIndex(null);
    setBeneListFrozenSnapshot(null);
    setSeedEmptyRow(false);
  }, []);

  useEffect(() => {
    if (!isLoaded && accountId) {
      if (selectedLinkedAccountId) {
        dispatch(AccountManagementDataActions.requestAccountDetails({ accountId: selectedLinkedAccountId }));
        dispatch(AccountManagementDataActions.requestAccountManagementDetails({ accountId: selectedLinkedAccountId }));
      } else {
        dispatch(AccountManagementDataActions.requestAccountDetails({ accountId }));
        dispatch(AccountManagementDataActions.requestAccountManagementDetails({ accountId }));
      }
    }
  }, [accountId, dispatch, updateData, isLoaded, selectedLinkedAccountId]);

  useEffect(() => {
    if (!manageMode) {
      setClientSaveValidationAttempt(0);
      setServerRejectionMessages([]);
    }
  }, [manageMode]);

  useEffect(() => {
    if (drawerIndex === null) {
      return;
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setDrawerIndex(null);
      }
    };
    document.addEventListener('keydown', onKey);
    const t = window.setTimeout(() => {
      if (drawerPanelRef.current) {
        drawerPanelRef.current.focus();
      }
    }, 0);
    return () => {
      document.removeEventListener('keydown', onKey);
      window.clearTimeout(t);
    };
  }, [drawerIndex]);

  useEffect(() => {
    return () => {
      dispatch(AccountManagementDataActions.resetMakeAccountUpdateResponseCode());
    };
  }, [dispatch]);

  useEffect(() => {
    if (!beneficiarySavePendingRef.current) {
      return;
    }
    if (isUpdateLoading) {
      return;
    }

    const payload = updateData as { errors?: string[]; payload?: { errors?: string[] } } | undefined;
    const payloadErrors = payload != null ? payload.errors : undefined;
    const nestedErrors =
      payload != null && payload.payload != null ? payload.payload.errors : undefined;
    const firstPayloadError =
      (Array.isArray(payloadErrors) && payloadErrors[0]) ||
      (Array.isArray(nestedErrors) && nestedErrors[0]);

    if (updateLoaded === false && updateData && typeof updateData === 'object' && 'error' in updateData) {
      const raw = (updateData as { error: unknown }).error;
      const message =
        (typeof raw === 'object' &&
          raw !== null &&
          'message' in raw &&
          typeof (raw as { message?: string }).message === 'string' &&
          (raw as { message: string }).message) ||
        (typeof raw === 'string' ? raw : null) ||
        'Unable to save beneficiaries. Please try again.';
      console.error('[Beneficiaries] Account update request failed', raw);
      notifications.error(message);
      beneficiarySavePendingRef.current = false;
      dispatch(AccountManagementDataActions.resetMakeAccountUpdateResponseCode());
      return;
    }

    if (updateLoaded === true && (updateResponseCode === 0 || updateResponseCode === 99)) {
      console.log('[Beneficiaries] Beneficiary update completed', { responseCode: updateResponseCode });
      // API returns 0 or 99 for successful updates; both result in immediate beneficiary changes (see accountManagement middleware).
      notifications.success('Beneficiary information saved successfully.');
      setServerRejectionMessages([]);
      beneficiarySavePendingRef.current = false;
      setManageMode(false);
      setDrawerIndex(null);
      setBeneListFrozenSnapshot(null);
      setSeedEmptyRow(false);
      dispatch(AccountManagementDataActions.resetMakeAccountUpdateResponseCode());
      window.setTimeout(() => {
        scrollAccountManagementBlockIntoView();
      }, 0);
      return;
    }

    if (
      updateLoaded === true &&
      updateResponseCode !== undefined &&
      updateResponseCode !== 0 &&
      updateResponseCode !== 99
    ) {
      const description = getResponseCodeDescription(
        updateResponseCode,
        typeof firstPayloadError === 'string'
          ? firstPayloadError
          : 'Unable to save beneficiaries. Please review your entries and try again.'
      );
      const listFromApi: string[] = [];
      if (Array.isArray(payloadErrors)) {
        listFromApi.push(...payloadErrors.map(e => (typeof e === 'string' ? e : String(e))));
      }
      if (Array.isArray(nestedErrors)) {
        listFromApi.push(...nestedErrors.map(e => (typeof e === 'string' ? e : String(e))));
      }
      if (listFromApi.length === 0 && typeof firstPayloadError === 'string') {
        listFromApi.push(firstPayloadError);
      }
      setServerRejectionMessages(listFromApi.length > 0 ? listFromApi : [description]);
      console.warn('[Beneficiaries] Beneficiary update rejected', { responseCode: updateResponseCode, description });
      notifications.error(description);
      beneficiarySavePendingRef.current = false;
      dispatch(AccountManagementDataActions.resetMakeAccountUpdateResponseCode());
    }
  }, [
    dispatch,
    isUpdateLoading,
    notifications,
    updateData,
    updateLoaded,
    updateResponseCode,
  ]);

  const initialBeneficiariesValues: AccountManagementBeneficiaryUpdateModel = useMemo(() => {
    if (beneArr.length > 0) {
      return {
        beneficiaries: beneArr.map(b => ({
          ...b,
          address: {
            ...b.address,
            country: normalizeCountryCodeForForm(b.address && b.address.country) || '',
          },
        })),
        max: 10,
      };
    }
    if (seedEmptyRow) {
      return {
        beneficiaries: [createEmptyBeneficiaryRow()],
        max: 10,
      };
    }
    return { beneficiaries: [], max: 10 };
  }, [beneArr, seedEmptyRow]);

  const onSubmit = useCallback(
    (values: AccountManagementBeneficiaryUpdateModel) => {
      const cleanDateBeneficiaries = values.beneficiaries.map((b: AccountManagementBeneficiaryModel) => {
        const row = { ...b };
        if (row.dateOfBirth && row.beneficiaryType === BeneficiaryType.Individual) {
          row.dateOfBirth = cleanDateModel(row.dateOfBirth);
        }
        if (row.dateOfBirth && row.beneficiaryType === BeneficiaryType.Trust) {
          row.dateOfBirth = null;
        }
        return row;
      });

      const updateValues = {
        item: 'Beneficiaries',
        beneficiaries: cleanDateBeneficiaries,
      };

      if (effectiveAccountId == null) {
        console.error('[Beneficiaries] Missing account id; cannot save beneficiaries');
        notifications.error('Unable to save beneficiaries. Account is not available.');
        return;
      }

      setServerRejectionMessages([]);
      beneficiarySavePendingRef.current = true;
      dispatch(
        AccountManagementDataActions.requestMakeAccountUpdate({
          accountId: effectiveAccountId,
          request: updateValues,
        })
      );
    },
    [dispatch, effectiveAccountId, notifications]
  );

  // TODO: Review localStorage usage
  const onValidate = (values: AccountManagementBeneficiaryUpdateModel) => {
    const errors: Record<string, any> = {};

    // Validate beneficiary percentages
    const percentageErrors = validateBeneficiaryPercentages(values.beneficiaries);
    Object.assign(errors, percentageErrors);

    // Validate individual beneficiary fields
    values.beneficiaries.forEach((b, i) => {
      // Validate address
      const addressErrors = validateAddress(
        {
          ...b.address,
          address1: b.address.address1 || '',
          address2: b.address.address2 || '',
          city: b.address.city || '',
          state: b.address.state || '',
          postalCode: b.address.postalCode || '',
          country: b.address.country || '',
        },
        Config.tradingApi,
        apiToken,
        `beneficiaries[${i}].address`
      );
      Object.assign(errors, addressErrors);

      if (b.beneficiaryType === BeneficiaryType.Individual) {
        if (!b.firstName) {
          errors[`beneficiaries[${i}].firstName`] = getRequiredError(b.firstName);
        }
        if (b.middleName && b.middleName.length > 20) {
          errors[`beneficiaries[${i}].middleName`] = getInvalidError(b.middleName);
        }
        if (!b.lastName) {
          errors[`beneficiaries[${i}].lastName`] = getRequiredError(b.lastName);
        }
        const ssnItinErr = getSsnItinError(b.ssnSecret);
        if (ssnItinErr) {
          errors[`beneficiaries[${i}].ssnSecret`] = ssnItinErr;
        }
        if (!b.relationship) {
          errors[`beneficiaries[${i}].relationship`] = getRequiredError(b.relationship);
        }
        if (!b.dateOfBirth.day) {
          errors[`beneficiaries[${i}].dateOfBirth`] = getRequiredError(b.dateOfBirth.day);
        }
        if (!b.dateOfBirth.month) {
          errors[`beneficiaries[${i}].dateOfBirth`] = getRequiredError(b.dateOfBirth.month);
        }
        if (!b.dateOfBirth.year) {
          errors[`beneficiaries[${i}].dateOfBirth`] = getRequiredError(b.dateOfBirth.year);
        }
      }

      if (b.beneficiaryType === BeneficiaryType.Trust) {
        if (!b.trustName) {
          errors[`beneficiaries[${i}].trustName`] = getRequiredError(b.trustName);
        }
        if (!b.einSecret) {
          errors[`beneficiaries[${i}].einSecret`] = getRequiredError(b.einSecret);
        }
      }

      if (!b.beneficiaryAccountType || !b.beneficiaryType) {
        errors[`beneficiaries[${i}].beneficiaryAccountType`] = getRequiredError(b.beneficiaryAccountType);
        errors[`beneficiaries[${i}].beneficiaryType`] = getRequiredError(b.beneficiaryType);
      }

      if (!b.percent) {
        errors[`beneficiaries[${i}].percent`] = getRequiredError(b.percent);
      }
      if (b.percent && !_.isInteger(b.percent)) {
        errors[`beneficiaries[${i}].percent`] = getInvalidError(b.percent);
      }
    });

    // Remove any undefined errors
    Object.keys(errors).forEach(key => (errors[key] === undefined ? delete errors[key] : {}));

    return errors;
  };

  const readOnlyBeneficiaryList = useMemo(() => {
    if (!beneficiaries || beneficiaries.length === 0) {
      return null;
    }

    const primary: { b: AccountManagementBeneficiaryModel; i: number }[] = [];
    const contingent: { b: AccountManagementBeneficiaryModel; i: number }[] = [];
    const uncategorized: { b: AccountManagementBeneficiaryModel; i: number }[] = [];

    beneficiaries.forEach((b, i) => {
      if (b.beneficiaryAccountType === BeneficiaryAccountType.Primary) {
        primary.push({ b, i });
      } else if (b.beneficiaryAccountType === BeneficiaryAccountType.Contingent) {
        contingent.push({ b, i });
      } else {
        uncategorized.push({ b, i });
      }
    });

    const hasPrimary = primary.length > 0;
    const hasContingent = contingent.length > 0;
    const hasMainGrid = hasPrimary || hasContingent;
    const hasUnc = uncategorized.length > 0;

    const renderReadOnlyCard = (b: AccountManagementBeneficiaryModel, i: number) => {
      const isPrimary = b.beneficiaryAccountType === BeneficiaryAccountType.Primary;
      const relationshipIcon =
        b.relationship === 'Spouse' ? (
          <i className="fal fa-heart text-muted" aria-hidden />
        ) : b.relationship === 'Child' ? (
          <i className="fal fa-child text-muted" aria-hidden />
        ) : b.relationship === 'Parent' ? (
          <i className="fal fa-user-friends text-muted" aria-hidden />
        ) : b.relationship === 'Trust' ? (
          <i className="fal fa-handshake text-muted" aria-hidden />
        ) : b.relationship && !['Spouse', 'Child', 'Parent', 'Trust'].includes(b.relationship) ? (
          <i className="fal fa-user text-muted" aria-hidden />
        ) : null;

      return (
        <ReadOnlyCard key={`bene-read-${i}`}>
          <ReadOnlyCardHeader>
            <ReadOnlyNameCluster>
              <ReadOnlyNameText>
                {b.beneficiaryType === BeneficiaryType.Individual
                  ? `${b.firstName || ''} ${b.lastName || ''}`.trim() || '—'
                  : b.trustName || '—'}
              </ReadOnlyNameText>
              <span
                className={`badge ${isPrimary ? 'badge-primary' : 'badge-contingent'}`}
              >
                {b.beneficiaryAccountType}
                {b.beneficiaryType ? ` · ${b.beneficiaryType}` : ''}
              </span>
            </ReadOnlyNameCluster>
            {relationshipIcon ? (
              <span className="flex-shrink-0" title={b.relationship || undefined}>
                {relationshipIcon}
              </span>
            ) : null}
          </ReadOnlyCardHeader>

          {b.beneficiaryType === BeneficiaryType.Individual ? (
            <>
              <ReadOnlyDetailsColumns>
                <ReadOnlyDetailsColumn>
                  <div className="small text-uppercase text-muted mb-2">Personal</div>
                  <DefRow>
                    <DefDt>Relationship</DefDt>
                    <DefDd>
                      <span className="mute">{b.relationship || '—'}</span>
                    </DefDd>
                  </DefRow>
                  <DefRow>
                    <DefDt>Date of birth</DefDt>
                    <DefDd>
                      {b.dateOfBirth ? `${b.dateOfBirth.month}/${b.dateOfBirth.day}/${b.dateOfBirth.year}` : '—'}
                    </DefDd>
                  </DefRow>
                  <DefRow>
                    <DefDt>SSN/ITIN</DefDt>
                    <DefDd>{b.ssnSecret ? maskSSN(b.ssnSecret) : '—'}</DefDd>
                  </DefRow>
                  <ShareRow>
                    <DefDt>Share</DefDt>
                    <DefDd>
                      <ShareValue>{b.percent != null ? `${b.percent}%` : '—'}</ShareValue>
                    </DefDd>
                  </ShareRow>
                </ReadOnlyDetailsColumn>
                <ReadOnlyDetailsColumn>
                  <div className="small text-uppercase text-muted mb-2">Address</div>
                  <ReadOnlyAddressText className="mute">
                    <BeneficiaryAddress
                      address={{
                        address1: b.address.address1 || '',
                        address2: b.address.address2 || '',
                        city: b.address.city || '',
                        state: b.address.state || '',
                        postalCode: b.address.postalCode || '',
                        country: b.address.country || '',
                      }}
                    />
                  </ReadOnlyAddressText>
                </ReadOnlyDetailsColumn>
              </ReadOnlyDetailsColumns>
            </>
          ) : (
            <ReadOnlyDetailsColumn>
              <div className="small text-uppercase text-muted mb-2">Trust</div>
              <DefRow>
                <DefDt>Trust name</DefDt>
                <DefDd>{b.trustName || '—'}</DefDd>
              </DefRow>
              <DefRow>
                <DefDt>EIN</DefDt>
                <DefDd>{b.einSecret || '—'}</DefDd>
              </DefRow>
              <ShareRow>
                <DefDt>Share</DefDt>
                <DefDd>
                  <ShareValue>{b.percent != null ? `${b.percent}%` : '—'}</ShareValue>
                </DefDd>
              </ShareRow>
              <div className="small text-uppercase text-muted mb-2 mt-3">Address</div>
              <ReadOnlyAddressText className="mute">
                <BeneficiaryAddress
                  address={{
                    address1: b.address.address1 || '',
                    address2: b.address.address2 || '',
                    city: b.address.city || '',
                    state: b.address.state || '',
                    postalCode: b.address.postalCode || '',
                    country: b.address.country || '',
                  }}
                />
              </ReadOnlyAddressText>
            </ReadOnlyDetailsColumn>
          )}
          <ReadOnlyCardActions>
            <ReadOnlyEditButton
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                setManageFormKey(k => k + 1);
                setManageMode(true);
                setSeedEmptyRow(false);
                setDrawerIndex(i);
              }}
            >
              <i className="fal fa-pen" aria-hidden /> Edit
            </ReadOnlyEditButton>
          </ReadOnlyCardActions>
        </ReadOnlyCard>
      );
    };

    return (
      <>
        {hasMainGrid && (
          <BeneReadOnlyLayout $twoColumn={hasPrimary && hasContingent}>
            {hasPrimary && (
              <BeneReadOnlyColumn>
                <SectionHeading>Primary beneficiaries</SectionHeading>
                <BeneReadOnlyCardStack>
                  {primary.map(({ b, i }) => renderReadOnlyCard(b, i))}
                </BeneReadOnlyCardStack>
              </BeneReadOnlyColumn>
            )}
            {hasContingent && (
              <BeneReadOnlyColumn>
                <SectionHeading>Contingent beneficiaries</SectionHeading>
                <BeneReadOnlyCardStack>
                  {contingent.map(({ b, i }) => renderReadOnlyCard(b, i))}
                </BeneReadOnlyCardStack>
              </BeneReadOnlyColumn>
            )}
          </BeneReadOnlyLayout>
        )}
        {hasUnc && (
          <BeneReadOnlyColumn
            style={{
              marginTop: hasMainGrid ? '1.5rem' : 0,
            }}
          >
            {!(hasPrimary || hasContingent) ? <SectionHeading>Beneficiaries</SectionHeading> : null}
            <BeneReadOnlyCardStack>
              {uncategorized.map(({ b, i }) => renderReadOnlyCard(b, i))}
            </BeneReadOnlyCardStack>
          </BeneReadOnlyColumn>
        )}
      </>
    );
  }, [beneficiaries]);

  const detailsErrorMessage =
    detailsError == null
      ? null
      : typeof detailsError === 'string'
        ? detailsError
        : typeof detailsError === 'object' &&
            detailsError !== null &&
            'message' in detailsError &&
            typeof (detailsError as { message?: string }).message === 'string'
          ? (detailsError as { message: string }).message
          : 'Unable to load beneficiary information. Please refresh the page or try again later.';

  if (detailsError) {
    return (
      <div className="screen-body-section beneficiary-account-management">
        <div className="alert alert-danger" role="alert" aria-live="assertive">
          {detailsErrorMessage}
        </div>
      </div>
    );
  }

  const showBlockingLoader = isUpdateLoading || detailsFetching;

  return (
    <>
      {showBlockingLoader && (
        <div className="screen-body-section beneficiary-account-management">
          <div className="beneficiaries-list">
            <h2 className="fields-title">Beneficiaries</h2>
          </div>
          <Loading />
        </div>
      )}
      {!showBlockingLoader && (
        <div className="screen-body-section beneficiary-account-management">
          {beneArr.length > 0 && !manageMode && (
            <div className="beneficiaries-list">
              <h2 className="fields-title">
                <i className="fal fa-users dashboard-icon" /> Beneficiaries
              </h2>
              <BeneficiariesReadOnlyContent>{readOnlyBeneficiaryList}</BeneficiariesReadOnlyContent>

              <BeneReadOnlyFooter>
                <BeneFooterRemoveButton
                  onClick={e => {
                    e.preventDefault();
                    setShowRemoveAll(true);
                  }}
                >
                  Remove all beneficiaries
                </BeneFooterRemoveButton>
                <BeneFooterPrimaryButton
                  onClick={e => {
                    e.preventDefault();
                    setManageFormKey(k => k + 1);
                    setManageMode(true);
                    setSeedEmptyRow(false);
                    setDrawerIndex(null);
                  }}
                >
                  Manage beneficiaries
                </BeneFooterPrimaryButton>
              </BeneReadOnlyFooter>
            </div>
          )}
          {beneArr.length === 0 && !manageMode && (
            <div className="beneficiaries-list">
              <h2 className="fields-title">
                <i className="fal fa-users dashboard-icon" /> Beneficiaries
              </h2>
              <EmptyBeneficiaryState role="status" aria-live="polite">
                <i className="fal fa-user-friends fa-2x mb-3 d-block text-muted" aria-hidden />
                <p className="mb-2 font-weight-medium text-dark">No beneficiaries on file</p>
                <p className="mb-0 small">
                  Add primary and contingent beneficiaries so this retirement account reflects your wishes. Total
                  primary shares should equal 100%; if you add contingent beneficiaries, their shares should total 100%
                  as well.
                </p>
              </EmptyBeneficiaryState>
              <BeneReadOnlyFooter>
                <BeneFooterPrimaryButton
                  onClick={e => {
                    e.preventDefault();
                    setManageFormKey(k => k + 1);
                    setSeedEmptyRow(true);
                    setManageMode(true);
                    setDrawerIndex(0);
                  }}
                >
                  Add beneficiaries
                </BeneFooterPrimaryButton>
              </BeneReadOnlyFooter>
            </div>
          )}
          {manageMode && (
            <>
              <p className="mb-2">
                <button
                  type="button"
                  className="btn btn-blend"
                  onClick={exitManageMode}
                  aria-label="Go back to beneficiaries list"
                >
                  <i className="fal fa-arrow-left" aria-hidden />
                  Go back
                </button>
              </p>
              <FormMaxWidth>
              <p>
                <strong>Save</strong> when you’re ready for your changes to apply. Use <strong>Edit</strong> on a row to
                open details in the side panel; the row list here doesn’t update live while that panel is open.
              </p>
              <div className="mb-3 text-muted" id="beneficiary-form-help">
                <p className="mb-2">
                  <strong>Primary</strong> shares must add up to <strong>100%</strong>. If save is blocked, check the
                  messages above and fix any row that still needs attention.
                </p>
                <p className="mb-0">
                  If you add <strong>contingent</strong> beneficiaries, primary shares must be complete at 100% first,
                  then contingent shares must add up to 100%. Contingent entries do not apply until that rule is
                  satisfied.
                </p>
              </div>

              <div className="beneficiary-form">
                <Formik
                  key={manageFormKey}
                  initialValues={initialBeneficiariesValues}
                  enableReinitialize={false}
                  onSubmit={onSubmit}
                  validate={onValidate}
                  validateOnChange={true}
                  validateOnBlur={true}
                >
                  {({ values, errors, submitCount, validateForm, submitForm, setFieldTouched }) => {
                    const listForDisplay: AccountManagementBeneficiaryModel[] =
                      drawerIndex !== null &&
                      beneListFrozenSnapshot != null &&
                      beneListFrozenSnapshot.length === values.beneficiaries.length
                        ? beneListFrozenSnapshot
                        : values.beneficiaries;
                    const firstPrimaryIdx = listForDisplay.findIndex(
                      b => b.beneficiaryAccountType === BeneficiaryAccountType.Primary
                    );
                    const firstContingentIdx = listForDisplay.findIndex(
                      b => b.beneficiaryAccountType === BeneficiaryAccountType.Contingent
                    );

                    const primaryRows = values.beneficiaries.filter(
                      b => b.beneficiaryAccountType === BeneficiaryAccountType.Primary
                    );
                    const contingentRows = values.beneficiaries.filter(
                      b => b.beneficiaryAccountType === BeneficiaryAccountType.Contingent
                    );
                    const primaryTotal = _.sumBy(primaryRows, b => _.toNumber(b.percent) || 0);
                    const contingentTotal = _.sumBy(contingentRows, b => _.toNumber(b.percent) || 0);
                    const hasContingent = contingentRows.length > 0;

                    const blockerLines = buildClientSaveBlockerLines(
                      values,
                      errors as Record<string, unknown>
                    );

                    const onSaveClick = async (e: React.MouseEvent) => {
                      e.preventDefault();
                      setClientSaveValidationAttempt(a => a + 1);
                      setServerRejectionMessages([]);
                      const validationErrors = await validateForm();
                      const errKeys = Object.keys(validationErrors);
                      if (errKeys.length > 0) {
                        errKeys.forEach(path => {
                          setFieldTouched(path, true, false);
                        });
                        const firstBene = errKeys.find(
                          k => k.startsWith('beneficiaries[') && k !== 'beneficiaries'
                        );
                        if (firstBene) {
                          const m = firstBene.match(/^beneficiaries\[(\d+)\]/);
                          if (m) {
                            setDrawerIndex(Number(m[1]));
                          }
                        }
                        scrollToFirstBeneficiaryFormError(validationErrors as Record<string, unknown>);
                        return;
                      }
                      await submitForm();
                    };

                    return (
                    <div className="form-section">
                      <BeneListSnapshotEffect
                        beneficiaries={values.beneficiaries}
                        drawerIndex={drawerIndex}
                        setBeneListFrozenSnapshot={setBeneListFrozenSnapshot}
                      />
                      {serverRejectionMessages.length > 0 && (
                        <div className="alert alert-danger" role="alert" aria-live="assertive" id="beneficiary-server-errors">
                          <p className="mb-2 font-weight-bold">The update could not be completed.</p>
                          <p className="mb-2 small text-muted">Review the messages below. Fields may not be highlighted for server-side issues.</p>
                          <ul className="mb-0 pl-3">
                            {serverRejectionMessages.map((line, i) => (
                              <li key={`srv-${i}`}>{line}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      <BeneFormStatusPanel id="beneficiary-form-status" role="region" aria-label="Share allocation and validation">
                        <BeneFormStatusPanelHeader>Before you save</BeneFormStatusPanelHeader>
                        <BeneFormStatusPanelBody>
                          <div>
                            <strong>Primary</strong> total: {primaryTotal}% (target 100%)
                          </div>
                          {hasContingent ? (
                            <div className="mt-1">
                              <strong>Contingent</strong> total: {contingentTotal}% (target 100%)
                            </div>
                          ) : null}
                          {blockerLines.length > 0 && (
                            <BeneFormStatusBlockerPart role="status" aria-live="polite" id="beneficiary-save-blockers">
                              <div className="font-weight-bold mb-1 small" style={{ color: 'rgba(255,255,255,0.85)' }}>
                                What&apos;s blocking save
                              </div>
                              <ul className="mb-0 pl-3 small" style={{ lineHeight: 1.45 }}>
                                {blockerLines.map((line, i) => (
                                  <li key={`bl-${i}`}>{line}</li>
                                ))}
                              </ul>
                            </BeneFormStatusBlockerPart>
                          )}
                          {Object.keys(errors).length > 0 && (submitCount > 0 || clientSaveValidationAttempt > 0) && (
                            <p className="mb-0 mt-2 small text-muted" role="status">
                              Check highlighted fields, or use the list above to find each issue.
                            </p>
                          )}
                        </BeneFormStatusPanelBody>
                      </BeneFormStatusPanel>
                      <FieldArray
                        name="beneficiaries"
                        render={arrayHelpers => {
                          const removeBeneAt = (index: number) => {
                            const isLastInMemoryRow =
                              values.beneficiaries.length === 1 && beneArr.length === 0;
                            setDrawerIndex(d => {
                              if (d === null) {
                                return null;
                              }
                              if (d === index) {
                                return null;
                              }
                              if (d > index) {
                                return d - 1;
                              }
                              return d;
                            });
                            if (isLastInMemoryRow) {
                              setManageMode(false);
                              setBeneListFrozenSnapshot(null);
                              setSeedEmptyRow(false);
                            } else {
                              setBeneListFrozenSnapshot(prev => {
                                if (prev == null) {
                                  return null;
                                }
                                if (index < 0 || index >= prev.length) {
                                  return prev;
                                }
                                return prev.filter((_, i) => i !== index);
                              });
                            }
                            arrayHelpers.remove(index);
                            clearAllBeneficiaryUspsSessionStorage();
                          };

                          return (
                          <>
                            {listForDisplay.length === 0 && (
                              <p className="text-muted small mb-3" role="status">
                                No beneficiaries in this list yet. Use <strong>Add beneficiary</strong> below to get
                                started, then use <strong>Save</strong> when everything is valid.
                              </p>
                            )}
                            {listForDisplay.map((beneficiary, index) => {
                              const hasRowError = beneficiaryIndexHasClientErrors(
                                index,
                                errors as Record<string, unknown>
                              );
                              return (
                              <React.Fragment key={`beneficiary${index}`}>
                                {index === firstPrimaryIdx && firstPrimaryIdx >= 0 && (
                                  <SectionHeading>Primary beneficiaries</SectionHeading>
                                )}
                                {index === firstContingentIdx && firstContingentIdx >= 0 && (
                                  <SectionHeading>Contingent beneficiaries</SectionHeading>
                                )}
                                <ManageBeneListRow $hasError={hasRowError} data-bene-form-index={String(index)}>
                                  <ManageBeneListRowMain>
                                    <ManageBeneListRowName>{getBeneListRowTitle(beneficiary)}</ManageBeneListRowName>
                                    <ManageBeneListRowMeta>
                                      {beneficiary.beneficiaryAccountType || '—'}
                                      {beneficiary.beneficiaryType ? ` · ${beneficiary.beneficiaryType}` : ''}
                                      {beneficiary.percent != null
                                        ? ` · Share: ${beneficiary.percent}%`
                                        : ' · Share: —'}
                                    </ManageBeneListRowMeta>
                                    <BeneStatusPill $ok={!hasRowError}>
                                      {hasRowError ? 'Needs attention' : 'Complete'}
                                    </BeneStatusPill>
                                  </ManageBeneListRowMain>
                                  <ManageBeneListRowActions>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-primary"
                                      onClick={() => {
                                        setDrawerIndex(index);
                                      }}
                                    >
                                      {drawerIndex === index ? 'Continue editing' : 'Edit details'}
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-danger"
                                      style={{ width: '100%' }}
                                      disabled={drawerIndex !== null}
                                      title={
                                        drawerIndex !== null
                                          ? 'Close the side panel, or use Remove in the panel for this entry'
                                          : undefined
                                      }
                                      onClick={() => {
                                        removeBeneAt(index);
                                      }}
                                    >
                                      Remove beneficiary
                                    </button>
                                  </ManageBeneListRowActions>
                                </ManageBeneListRow>
                              </React.Fragment>
                            );
                            })}

                            {values.max && values.beneficiaries.length < values.max && (
                              <p>
                                <button
                                  onClick={event => {
                                    event.currentTarget.blur();
                                    event.preventDefault();
                                    const nextIndex = values.beneficiaries.length;
                                    arrayHelpers.push(createEmptyBeneficiaryRow());
                                    setDrawerIndex(nextIndex);
                                  }}
                                  type="button"
                                >
                                  <AddIcon inverse={true} />
                                  <strong>Add Beneficiary</strong>
                                </button>{' '}
                                <Text
                                  id={`available-counts.${values.beneficiaries.length > 0 ? 'partial' : 'all'}`}
                                  data={{ max: values.max, left: values.max - values.beneficiaries.length }}
                                />
                              </p>
                            )}

                            {drawerIndex !== null &&
                            values.beneficiaries[drawerIndex] != null &&
                            drawerIndex < values.beneficiaries.length ? (
                              <ManageDrawerRoot $open={true} role="presentation" aria-hidden={false}>
                                <ManageDrawerBackdrop
                                  type="button"
                                  $open={true}
                                  tabIndex={-1}
                                  onClick={() => {
                                    setDrawerIndex(null);
                                  }}
                                  aria-label="Close side panel"
                                />
                                <ManageDrawerPanel
                                  $open={true}
                                  ref={drawerPanelRef}
                                  role="dialog"
                                  aria-modal="true"
                                  aria-labelledby="bene-manage-drawer-title"
                                  tabIndex={-1}
                                >
                                  <ManageDrawerHeader>
                                    <ManageDrawerTitle id="bene-manage-drawer-title">
                                      Beneficiary {drawerIndex + 1}
                                    </ManageDrawerTitle>
                                    <ManageDrawerHeaderActions>
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-link text-white-50 p-0"
                                        onClick={() => {
                                          setDrawerIndex(null);
                                        }}
                                        aria-label="Close side panel"
                                      >
                                        Close
                                      </button>
                                    </ManageDrawerHeaderActions>
                                  </ManageDrawerHeader>
                                  <ManageDrawerBody>
                                    <p className="small text-muted mb-3">
                                      <strong>Primary</strong> and <strong>contingent</strong> share rules (totals, order)
                                      still apply. Use <strong>Save</strong> when you’re done with your edits.
                                    </p>
                                    <BeneficiaryRowFormFields
                                      index={drawerIndex}
                                      clearer={clearer}
                                      showScrollNudge
                                    />
                                  </ManageDrawerBody>
                                  <ManageDrawerFooter>
                                    <DrawerFooterActions>
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-danger"
                                        onClick={() => {
                                          removeBeneAt(drawerIndex);
                                        }}
                                      >
                                        Remove beneficiary
                                      </button>
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-primary"
                                        disabled={isUpdateLoading}
                                        onClick={onSaveClick}
                                      >
                                        Save beneficiaries
                                      </button>
                                    </DrawerFooterActions>
                                  </ManageDrawerFooter>
                                </ManageDrawerPanel>
                              </ManageDrawerRoot>
                            ) : null}
                          </>
                          );
                        }}
                      />
                      <StickySaveFooter>
                        <BeneFormStickyActions className="form-actions">
                          <button
                            type="button"
                            className="btn btn-blend"
                            onClick={exitManageMode}
                            aria-label="Go back to beneficiaries list without saving"
                          >
                            <i className="fal fa-arrow-left" aria-hidden />
                            Go back
                          </button>
                          <button
                            type="button"
                            className="btn btn-primary"
                            disabled={isUpdateLoading}
                            onClick={onSaveClick}
                          >
                            Save beneficiaries
                          </button>
                        </BeneFormStickyActions>
                      </StickySaveFooter>
                    </div>
                    );
                  }}
                </Formik>
              </div>
              </FormMaxWidth>
            </>
          )}
        </div>
      )}
      <RemoveAllBeneficiaries setOpen={setShowRemoveAll} show={showRemoveAll} accountId={effectiveAccountId} />
    </>
  );
};
